import { AppointmentCreateData } from "@/Appointments/Models/Appointment/AppointmentCreateDataComposite";
import { EnumRecurrenceType } from "@/Appointments/Models/EnumRecurrenceType";
import { EnumAppointmentSource } from "@/Common/Models/Appointments/EnumAppointmentSource";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { PredefinedAppointmentSaveState } from "@/Patient/Components/Appointment/PredefinedAppointmentSave";
import { IPatientAppointmentModel } from "@/Patient/Models/UI/Appointment/IPatientAppointmentModel";

class PatientAppointmentAction extends BaseAction {
    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        super(onBeforeExecute, onBeforeHandleResponse);
    }

    public WillSearchOptimized(patientId: string): boolean {
        var uiModel = patientStore.PatientAppointmentModels[patientId];
        if (uiModel.get("SearchCriteria").count() > 1)
            return false;

        if (uiModel.get("IsClinicianFilterTab") === true) {
            // NbrOfServicesChecked -- Now Its a Radio Button so it should always work
            var sizeOfSecondList = uiModel.get("EmployeeServiceOrComboList").filter(s => s.Checked).size;
            var sizeOfAnyService = uiModel.get("AnyServiceList").filter(c => c.isSelected).size;

            if (sizeOfSecondList > 1 || sizeOfAnyService > 1)
                throw "Its a radio button for a reason, OptimizedSearch doesn't support Multi-Service Search"; // Each criteria must be 1service to use Optimized
        }

        return true;
    }

    public LoadPredefineAppointmentAvailability(model: IPatientAppointmentModel, searchDate: string, patientId: string): void {
        const includeRuleSet = model.get("IncludeRuleSets");

        // only allow global search when flag is enabled, service or combo is selected, and the IncludeRuleSets (≡) button is not active
        const useGlobalAvailabilitySearch = cacheStore.ClinicSettings.EnablePatientChartGlobalAvailabilitySearch
            && model.get("SearchCriteria").count(p => p.Model.AnyServiceList?.length > 0) === 0
            && !includeRuleSet;

        // Add an Id to the Search to remerge both search
        let searchId: number = 0;
        if (patientStore.searchId != null) {
            searchId = patientStore.searchId;
            patientStore.searchId = patientStore.searchId + 1;
        }

        if (useGlobalAvailabilitySearch) {
            this.SearchAppointmentFromGlobalPortalSearch(model, searchDate, patientId, searchId);
            return;
        }

        // Redirect to portal search
        if (!includeRuleSet && this.WillSearchOptimized(patientId) == true) {
            // Redirect to portal search
            this.SearchAppointmentFromPortalSearch(model, searchDate, patientId, searchId);
        }

        var searchCriteria = model.get("SearchCriteria");

        var requestData = {
            Criteria: [],
            SearchDate: null,
            AllowedDays: [],
            StartHour: 0,
            EndHour: 0,
            SearchId: 0,
            ExceptEmergencies: null,
            IncludeRuleSet: includeRuleSet,
            ComboId: null,
            IgnorePublishingRules: false
        };
        // THE GOOD EMPLOYEE ID IS THE CONTACT FACET ID
        // THE GOOD SERVICE ID IS THE RESOURCE DEFINITION ID
        for (var n = 0; n < searchCriteria.size; n++) {
            var sc = searchCriteria.get(n);
            var criteriaData = {
                EmployeesId: [],
                ServicesId: [],
                Position: 0,
                ExpectedDuration: 0,
                SiteId: ""
            };

            sc.Model.EmployeeList.forEach((employee: EmployeeUIModel) => {
                criteriaData.EmployeesId.push(employee.ContactFacetId);
            });

            sc.Model.ServiceList.forEach((checkableServiceDef: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>) => {
                criteriaData.ServicesId.push(checkableServiceDef.Model.ServiceDefinitionId);
            });

            criteriaData.Position = requestData.Criteria.length + 1;

            criteriaData.SiteId = sc.Model.AnyServiceList && sc.Model.AnyServiceList.length > 0 ? sc.Model.AnyServiceList[0].siteId : "";
            requestData.ExceptEmergencies = sc.Model.AnyServiceList != null && sc.Model.AnyServiceList.length > 0 ? sc.Model.AnyServiceList[0].anyExceptEmergencies : null;

            criteriaData.ExpectedDuration = model.get("CustomDuration");

            requestData.Criteria.push(criteriaData);
        }

        const momentOfTheWeekFilter = this.getMomentOfTheWeekFilterSection(model);

        requestData.StartHour = momentOfTheWeekFilter.startHour;
        requestData.EndHour = momentOfTheWeekFilter.endHour;
        requestData.SearchDate = searchDate;
        requestData.AllowedDays = momentOfTheWeekFilter.allowedDays;
        requestData.SearchId = searchId;
        requestData.ComboId = searchCriteria.first()?.Model.ServiceList?.firstOrDefault()?.Model.ComboId;
        requestData.IgnorePublishingRules = patientStore.shouldIgnorePublishingRules;

        this.Execute(ServerOperations.Server.Myle_Patient_Appointment_Search, requestData);
    }

    private SearchAppointmentFromPortalSearch(model: IPatientAppointmentModel, searchDate: string, patientId: string, searchId: number): void {
        var requestData = {
            ComboId: "",
            ServiceDefinitionId: "",
            EmployeeIds: [],
            PatientContactId: "",
            StartDate: searchDate,
            ExpectedDurationOverride: 0,
            SiteId: "",
            ExceptEmergencies: null,
            SearchId: 0,
            IncludeOptimizedResultDTODetails: true,
            AllowedDays: [],
            StartHour: 0,
            EndHour: 0,
            IgnorePublishingRules: false
        };
        var searchCriteria = model.get("SearchCriteria").toArray();

        requestData.ComboId = searchCriteria[0].Model.ServiceList != null && searchCriteria[0].Model.ServiceList.length > 0 && searchCriteria[0].Model.ServiceList[0].Model.ComboId != null ? searchCriteria[0].Model.ServiceList[0].Model.ComboId.toString() : "";
        requestData.ServiceDefinitionId = searchCriteria[0].Model.ServiceList != null && searchCriteria[0].Model.ServiceList.length > 0 ? searchCriteria[0].Model.ServiceList[0].Model.ServiceDefinitionId : "";
        requestData.EmployeeIds = searchCriteria[0].Model.AllEmployees == true ? [] : searchCriteria[0].Model.EmployeeList.map(x => x.ContactFacetId);
        requestData.PatientContactId = patientId;
        requestData.ExpectedDurationOverride = model.get("CustomDuration");
        if (searchCriteria[0].Model.AnyServiceList != null && searchCriteria[0].Model.AnyServiceList.length > 0) {
            requestData.SiteId = searchCriteria[0].Model.AnyServiceList[0].siteId;
            requestData.ExceptEmergencies = searchCriteria[0].Model.AnyServiceList[0].anyExceptEmergencies;
        }

        requestData.SearchId = searchId;

        const momentOfTheWeekFilter = this.getMomentOfTheWeekFilterSection(model);

        requestData.AllowedDays = momentOfTheWeekFilter.allowedDays;
        requestData.StartHour = momentOfTheWeekFilter.startHour;
        requestData.EndHour = momentOfTheWeekFilter.endHour;
        requestData.IgnorePublishingRules = patientStore.shouldIgnorePublishingRules;

        this.Execute(ServerOperations.Server.Portal_AppointmentSearch, requestData);
    }

    private SearchAppointmentFromGlobalPortalSearch(model: IPatientAppointmentModel, searchDate: string, patientId: string, searchId: number): void {
        const searchCriteria = model.get("SearchCriteria")
            .flatMap(criterion => {
                const employeeIds = criterion.Model.AllEmployees
                    ? []
                    : criterion.Model.EmployeeList.map(p => p.ContactFacetId);

                return criterion.Model.ServiceList.map(
                    service => ({
                        ComboId: service.Model.ComboId,
                        ServiceDefinitionId: !service.Model.ComboId ? service.Model.ServiceDefinitionId : null,
                        EmployeeIds: employeeIds
                    })
                );
            })
            .toArray();

        const momentOfTheWeekFilter = this.getMomentOfTheWeekFilterSection(model);

        // send the custom duration if the multi-search feature is off,
        // or there is only a single service selected on the "By Staff" tab
        const sendCustomDuration = !cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch
            || (model.get("IsClinicianFilterTab") && searchCriteria.length < 2);

        const customDuration = sendCustomDuration ? model.get("CustomDuration") : null;

        this.Execute(
            ServerOperations.Server.Portal_GlobalAppointmentSearch,
            {
                SearchId: searchId,
                SearchCriteria: searchCriteria,
                PatientContactId: patientId,
                StartDate: searchDate,
                AllowedDays: momentOfTheWeekFilter.allowedDays,
                StartHour: momentOfTheWeekFilter.startHour,
                EndHour: momentOfTheWeekFilter.endHour,
                ExpectedDurationOverride: customDuration,
                IgnorePublishingRules: patientStore.shouldIgnorePublishingRules
            }
        );
    }

    public PortalAppointmentSave(model: PredefinedAppointmentSaveState, patientContactId: string, force: boolean = false): void {
        let appointmentContactNotification = calendarStore.getAppointmentContactPointNotification(
            patientStore.getCurrentPatient(),
            model.contactPointNotificationIds
        );

        let requestData = {
            EncryptedSaveKey: model.encryptedSaveKey,
            BookingTokenInfo: model.bookingTokenInfo,
            PatientContactId: patientContactId,
            Source: EnumAppointmentSource.Myle_PatientProfile,
            PatientNote: model.predefinedAppointments[0].Subject,
            MyleNote: model.notes,
            ReferralLicenceNumber: model.selectedReferalDoctor == null ? null : model.selectedReferalDoctor.LicenceNumber,
            ReferralPhone: model.referalPhoneNumber,
            AppointmentStateId: IsNullOrWhiteSpace(model.selectedAppointmentStateCode) ? null : model.appointmentStates.filter(x => x.LookupCode == model.selectedAppointmentStateCode)[0].LookupID,
            ForceSave: force,
            IsFromMyle: true,
            SkipPatientAppointmentNotification: model.notifyPatient != true,
            IsTeleconsultation: model.isTeleconsultation,
            ContactPointNotificationList: appointmentContactNotification.contactPointNotificationList,
            HasNotificationContactPointSpecified: appointmentContactNotification.hasNotificationContactPointSpecified,
            IsPatientNotificationEnabled: model.notifyPatient,
            WaitForRulesetRecalculate: true
        };

        this.Execute(ServerOperations.Server.Portal_AppointmentSave, requestData);
    }

    public SavePredefinedAppointment(model: PredefinedAppointmentSaveState, patientContactId: string, forceSave: boolean = false): void {
        let AppointmentToArray = model.predefinedAppointments.map(apt => {
            let createAppointmentDto = AppointmentCreateData.FromPredefinedAppointmentItem(apt);

            this.setCreateAppointmentDto(createAppointmentDto, patientContactId, model);

            return createAppointmentDto;
        });

        var requestData = {
            Appointments: AppointmentToArray,
            forceDoubleBooking: forceSave,
            Source: EnumAppointmentSource.Myle_PatientProfile,
            RecurrenceType: EnumRecurrenceType.OneTime,
            RecurrenceEndDate: null,
            WaitForRulesetRecalculate: true
        };

        this.Execute(ServerOperations.Server.Myle_Calendar_CreateAppointment, requestData);
    }

    private setCreateAppointmentDto(
        createAppointmentDto: AppointmentCreateData,
        patientContactId: string,
        model: PredefinedAppointmentSaveState
    ): void {
        createAppointmentDto.PatientContactId = patientContactId;

        createAppointmentDto.AppointmentStateLookupId = IsNullOrWhiteSpace(model.selectedAppointmentStateCode)
            ? null
            : model.appointmentStates.filter(x => x.LookupCode == model.selectedAppointmentStateCode)[0].LookupID;

        createAppointmentDto.Notes = model.notes;

        createAppointmentDto.ReferralId = model.selectedReferalDoctor == null ? null : model.selectedReferalDoctor.LicenceNumber;

        createAppointmentDto.ReferralPhone = model.referalPhoneNumber;

        createAppointmentDto.CustomReferralDoctorName = model.searchReferralDoctorString;

        createAppointmentDto.IsPatientNotificationsEnabled = model.notifyPatient;

        createAppointmentDto.IsTeleconsultation = model.isTeleconsultation;

        let appointmentContactNotification = calendarStore.getAppointmentContactPointNotification(
            patientStore.getCurrentPatient(),
            model.contactPointNotificationIds
        );

        createAppointmentDto.ContactPointNotificationList = appointmentContactNotification.contactPointNotificationList;
        createAppointmentDto.HasNotificationContactPointSpecified = appointmentContactNotification.hasNotificationContactPointSpecified;
    }

    private getMomentOfTheWeekFilterSection(model: IPatientAppointmentModel): any {
        let allowedDays = [];

        model.get("WeekendCheck") && allowedDays.push(0);
        model.get("MondayCheck") && allowedDays.push(1);
        model.get("TuesdayCheck") && allowedDays.push(2);
        model.get("WednesdayCheck") && allowedDays.push(3);
        model.get("ThursdayCheck") && allowedDays.push(4);
        model.get("FridayCheck") && allowedDays.push(5);
        model.get("WeekendCheck") && allowedDays.push(6);

        const isAmChecked = model.get("AMCheck");
        const isPmChecked = model.get("PMCheck");

        let startHour = isAmChecked ? 0 : 12;
        let endHour = isPmChecked ? 24 : 12;

        if (!isAmChecked && !isPmChecked) {
            startHour = 0;
            endHour = 24;
        }

        return { allowedDays, startHour, endHour };
    }
}

export { PatientAppointmentAction };
